import { defineSlotRecipe } from '@chakra-ui/react';
import { badgeRecipe } from './Badge';
import { TextStyles, Radii, Colors, FontWeights, Space } from '../../types';

// The variants reuse Badge's --badge-* custom properties, so the badge variant styles
// are applied to the tag root.
const badgeVariant = ((badgeRecipe.variants as any)?.variant ?? {}) as Record<string, any>;

export const tagSlotRecipe = defineSlotRecipe({
  slots: ['root', 'label', 'closeTrigger', 'startElement', 'endElement'],
  base: {
    root: {
      gap: 1.5,
      px: Space.XS,
      py: Space['2XS'],
      ring: '1px',
      ringInset: 'inset',
      ringColor: 'var(--badge-shadow)',
      // Badge's variant objects only declare --badge-* custom properties, so Tag wires fill and text colour to
      // them as Badge's base does; otherwise Chakra's colorPalette.fg text and no background apply.
      bg: 'var(--badge-bg)',
      color: 'var(--badge-color)',
      minH: 'var(--tag-min-height)',
      display: 'inline-flex',
      flexGrow: '0',
      alignItems: 'center',
      alignSelf: 'center',
      verticalAlign: 'top',
      maxWidth: '100%',
      userSelect: 'none',
      focusVisibleRing: 'outside',
      textStyle: TextStyles.UI_DEFAULT,
      _disabled: {
        bg: Colors.SURFACE_DISABLED,
        color: Colors.TEXT_DISABLED,
        '--badge-iconColor': 'var(--udl-colors-text-disabled)',
      },
    },
    label: {
      fontWeight: FontWeights.MEDIUM,
      display: 'inline-flex',
      gap: Space.XS,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minW: 0,
    },
    closeTrigger: {
      ms: 0,
      opacity: '.72',
      boxSize: 'calc(var(--tag-min-height) - 0.5rem)',
      borderRadius: Radii.BASE,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      outline: 0,
      color: 'currentColor',
      focusVisibleRing: 'inside',
      focusRingWidth: '2px',
      _hover: {
        opacity: '1',
        bg: 'color-mix(in srgb, currentColor 8%, transparent)',
        color: 'var(--badge-color)',
      },
      _active: {
        opacity: '1',
        bg: 'color-mix(in srgb, currentColor 12%, transparent)',
        color: 'var(--badge-color)',
      },
      '& > svg': {
        alignSelf: 'center',
        fontSize: '1em',
      },
    },
  },
  variants: {
    // Stock sizes set root `px: 1.5`/`gap: 1` and a smaller label textStyle; ours keep base's and the root's.
    size: {
      sm: {
        root: {
          '--tag-min-height': 'spacing.6',
          gap: Space['2XS'],
          px: Space.XS,
          borderRadius: Radii.BASE,
          textStyle: TextStyles.UI_COMPACT,
        },
        label: { textStyle: 'none' },
        closeTrigger: {
          my: -1,
          me: -2,
          borderRadius: 'inherit',
          boxSize: 'var(--tag-min-height)',
        },
      },
      md: {
        root: {
          '--tag-min-height': 'spacing.7',
          gap: Space.SPACE_1_5,
          px: Space.XS,
          borderRadius: Radii.MD,
        },
        label: { textStyle: 'none' },
      },
      lg: {
        root: {
          '--tag-min-height': 'spacing.8',
          '--tag-padding-inline': 'spacing.sm',
          borderRadius: Radii.LG,
          h: 'var(--tag-min-height)',
          minH: 'var(--tag-min-height)',
          px: 'var(--tag-padding-inline)',
        },
        label: { textStyle: 'none' },
        closeTrigger: {
          me: '-2',
        },
      },
    },
    variant: {
      // Chakra's tag variants set `color` (e.g. outline's `colorPalette.fg`) and `bg` under the same key,
      // which beat base's `var(--badge-*)` values, so each variant redeclares them.
      subtle: { root: { ...badgeVariant.subtle, bg: 'var(--badge-bg)', color: 'var(--badge-color)' } },
      solid: { root: { ...badgeVariant.solid, bg: 'var(--badge-bg)', color: 'var(--badge-color)' } },
      outline: { root: { ...badgeVariant.outline, color: 'var(--badge-color)' } },
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'subtle',
  },
});
